import React, {useContext} from 'react';
import PropTypes from 'prop-types';
import getConfig from 'next/config';
import styled, {css, ThemeProvider} from 'styled-components';

import {getVideoFront} from '~/src/utilities/helpers/video';
import {getLifestyleFront} from '~/src/utilities/helpers/lifestyle';
import {SitemapContext} from '~/src/components/context/SitemapContext';
import {SettingsContext} from '~/src/components/context/SettingsContext';
import {getThemes} from '~/src/utilities/helpers/brand';
import {SiteArea} from '~/src/utilities/constants/navigation';
import {getCategoryUrl} from '~/src/utilities/helpers/link';

import {Breadcrumb as BreadcrumbMigrated} from '@components/molecules/breadcrumb';

import variables from '~/src/css/global.module.scss';

const {publicRuntimeConfig: config} = getConfig();

const sassVariableHelper = (variable) => variables[variable];

const Migrated = styled(({migration, ...props}) => <BreadcrumbMigrated {...props} />)`
    ${({migration}) => {
        if (migration.isLifestyle) {
            return css`
                color: ${sassVariableHelper('mainBackgroundColor')};
                margin: 0 10px 10px;
            `;
        }

        return css`
            margin: 0 10px 10px;
        `;
    }}
`;

const getUrl = ({fullUrlPath}, sitemap) => {
    switch (fullUrlPath) {
        case 'video':
            return getCategoryUrl(getVideoFront(sitemap.all).fullUrlPath);
        case 'lifestyle':
            return getCategoryUrl(getLifestyleFront(sitemap.all).fullUrlPath);
        default:
            return getCategoryUrl(fullUrlPath);
    }
};

const getCrumbs = (mainCategoryId, sitemap) => {
    let crumbs = [];
    let category = sitemap.getCategoryBy({categoryId: mainCategoryId});

    while (category) {
        crumbs.unshift({
            href: getUrl(category, sitemap),
            name: category.name
        });
        category = sitemap.getCategoryBy({categoryId: category.parent});
    }

    return crumbs.filter((crumb) => crumb);
};

const Themes = getThemes();
const PassThrough = (theme) => theme;

const Breadcrumb = ({mainCategoryId, title}) => {
    const sitemap = useContext(SitemapContext);
    const {siteArea} = useContext(SettingsContext);

    const crumbs = getCrumbs(mainCategoryId, sitemap);

    if (!crumbs.length) {
        return null;
    }

    const frontUrl = getCategoryUrl(config.frontCategory);
    if (!crumbs.some((crumb) => crumb.href === frontUrl)) {
        crumbs.unshift({href: frontUrl, name: 'Front'});
    }

    if (title) {
        crumbs.push({name: title});
    }

    const isLifestyle = siteArea === SiteArea.LIFESTYLE;
    const isVideoPage = siteArea === SiteArea.VIDEO;

    return (
        <ThemeProvider theme={isLifestyle ? Themes.LifestyleAlternative : isVideoPage ? Themes.Video : PassThrough}>
            <Migrated crumbs={crumbs} migration={{isLifestyle}} />
        </ThemeProvider>
    );
};

if (process.env.NODE_ENV === 'development') {
    Breadcrumb.propTypes = {
        mainCategoryId: PropTypes.number,
        title: PropTypes.string
    };
}

export default Breadcrumb;
